import React, { useState, useEffect } from "react";
import { Mail, MessageSquare, PhoneCall, Video, CalendarCheck } from "lucide-react";
import axios from "axios";
import io from "socket.io-client";

const socket = io("http://your-server.com"); // Replace with your backend URL

function EngageHubDashboard() {
  const [selectedAction, setSelectedAction] = useState(null);
  const [entityType, setEntityType] = useState(""); // Lead, Customer, Opportunity
  const [entityId, setEntityId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [callStatus, setCallStatus] = useState("");
  const [incomingCall, setIncomingCall] = useState(null);
  const [contacts, setContacts] = useState([]); // List of phone numbers for selected entity
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [enableTranscription, setEnableTranscription] = useState(false);

  const actions = [
    { id: "email", name: "Send Email", icon: <Mail className="w-6 h-6 text-blue-600" /> },
    { id: "whatsapp", name: "WhatsApp Message", icon: <MessageSquare className="w-6 h-6 text-green-600" /> },
    { id: "sms", name: "Send SMS", icon: <PhoneCall className="w-6 h-6 text-purple-600" /> },
    { id: "meeting", name: "Schedule Meeting", icon: <Video className="w-6 h-6 text-red-600" /> },
    { id: "reminder", name: "Set Reminder", icon: <CalendarCheck className="w-6 h-6 text-yellow-600" /> },
    { id: "call", name: "Make a Call", icon: <PhoneCall className="w-6 h-6 text-indigo-600" /> }, // ✅ New Call Action
  ];

  useEffect(() => {
    socket.on("incomingCall", (data) => {
      setIncomingCall(data);
    });

    return () => {
      socket.off("incomingCall");
    };
  }, []);

  // Fetch Phone Numbers Based on Entity Type & ID
  const fetchEntityContacts = async () => {
    if (!entityType || !entityId) return;
    try {
      const response = await axios.get(`/api/get-contacts?entityType=${entityType}&entityId=${entityId}`);
      setContacts(response.data); // Example response: [{ name: "John Doe", phone: "1234567890" }]
      setFilteredContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  // Handle Call
  const handleCall = async () => {
    if (!phoneNumber) return;
    try {
      setCallStatus("Calling...");
      const response = await axios.post("/api/make-call", { toNumber: phoneNumber, enableTranscription });
      if (response.data.success) {
        setCallStatus("Call in progress...");
      } else {
        setCallStatus("Call failed.");
      }
    } catch (error) {
      setCallStatus("Error making call.");
      console.error(error);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">EngageHub</h1>
      </div>

      {/* Action Panel */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {actions.map((action) => (
          <button
            key={action.id}
            className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center justify-center hover:bg-gray-100 transition"
            onClick={() => setSelectedAction(action.id)}
          >
            {action.icon}
            <span className="mt-2 text-gray-800 font-medium">{action.name}</span>
          </button>
        ))}
      </div>

      {/* Call Dialer */}
      {selectedAction === "call" && (
        <div className="mt-6 p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Make a Call</h2>

          {/* Entity Type Selection */}
          <label className="block mb-2 text-gray-700">Select Entity Type</label>
          <select
            className="border p-2 w-full rounded mb-4"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
          >
            <option value="">Select</option>
            <option value="lead">Lead</option>
            <option value="customer">Customer</option>
            <option value="opportunity">Opportunity</option>
          </select>

          {/* Entity ID Input */}
          <label className="block mb-2 text-gray-700">Enter Entity ID</label>
          <input
            type="text"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className="border p-2 w-full rounded mb-4"
            placeholder="Enter ID"
          />
          <button onClick={fetchEntityContacts} className="bg-blue-600 text-white px-4 py-2 rounded mb-4">
            Fetch Contacts
          </button>

          {/* Phone Number Selection */}
          <label className="block mb-2 text-gray-700">Select or Enter Phone Number</label>
          <input
            type="text"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="border p-2 w-full rounded"
            placeholder="Enter phone number"
            disabled={!entityType || !entityId}
          />

          {/* Contact Suggestions */}
          {contacts.length > 0 && (
            <ul className="border rounded mt-2 max-h-40 overflow-y-auto">
              {contacts.map(contact => (
                <li
                  key={contact.phone}
                  onClick={() => setPhoneNumber(contact.phone)}
                  className="p-2 cursor-pointer hover:bg-gray-100"
                >
                  {contact.name} - {contact.phone}
                </li>
              ))}
            </ul>
          )}

          {/* Transcription Toggle */}
          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="transcription"
              checked={enableTranscription}
              onChange={() => setEnableTranscription(!enableTranscription)}
              className="mr-2"
            />
            <label htmlFor="transcription" className="text-gray-700">Enable Call Transcription</label>
          </div>

          <div className="mt-4">
            <button
              onClick={handleCall}
              className={`px-4 py-2 rounded ${phoneNumber ? "bg-indigo-600 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
              disabled={!phoneNumber}
            >
              Dial
            </button>
            {callStatus && <p className="mt-2 text-gray-600">{callStatus}</p>}
          </div>

          <button
            onClick={() => setSelectedAction(null)}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
          >
            Close
          </button>
        </div>
      )}

      {/* Incoming Call Popup */}
      {incomingCall && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <h2 className="text-xl font-semibold text-gray-900">📞 Incoming Call</h2>
            <p className="text-gray-700">{incomingCall.from}</p>
            <button className="bg-green-600 text-white px-4 py-2 rounded mt-4 mr-2">Accept</button>
            <button
              onClick={() => setIncomingCall(null)}
              className="bg-red-600 text-white px-4 py-2 rounded mt-4"
            >
              Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EngageHubDashboard;